import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import StudentDetailModal from "./StudentDetailModal";

const JOB_ROLE_LABELS: Record<string, string> = {
  // 공통 / MusicLabel
  CEO: 'CEO / 대표이사',
  AR: 'A&R 담당',
  Finance: '재무 담당',
  Marketing: '마케팅 담당',
  Legal: '법무 담당',
  // LiveEntertainment
  PD: '공연기획 PD',
  Ticketing: '티켓영업 MD',
  // CulturalFoundation
  Projects: '사업팀장',
  Fundraising: '후원개발팀',
  Evaluation: '사업평가위원',
  // WebtoonStudio
  Editor: '에디터',
  Contract: '계약 MD',
  // ContentAgency
  Creative: '크리에이티브 디렉터',
  AE: '영업 AE',
  Production: '영상 PD',
  Data: '데이터 분석가',
};

interface Props {
  workspaceId: string;
}

interface StudentRow {
  userId: string;
  email: string;
  jobRole: string;
  joinedAt: string;
  capital: number;
  reputation: number;
  creditScore: number;
  taskCount: number;
  rank: string;
}

function getRank(rep: number) {
  if (rep >= 81) return "팀장";
  if (rep >= 61) return "과장";
  if (rep >= 41) return "대리";
  if (rep >= 21) return "사원";
  return "인턴";
}

function getRankColor(rep: number) {
  if (rep >= 81) return "text-yellow-500 bg-yellow-400/10 border-yellow-400/25";
  if (rep >= 61) return "text-[#E8375F] bg-[#E8375F]/10 border-[#E8375F]/25";
  if (rep >= 41) return "text-[#FF9F43] bg-[#FF9F43]/10 border-[#FF9F43]/25";
  if (rep >= 21) return "text-[#7A9BFF] bg-[#4B6CF7]/10 border-[#4B6CF7]/25";
  return "text-[#8B91A8] bg-[#8B91A8]/10 border-[#8B91A8]/25";
}

export default function StudentProgressDashboard({ workspaceId }: Props) {
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<"reputation" | "capital" | "tasks" | "joined">("reputation");
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [selectedStudent, setSelectedStudent] = useState<StudentRow | null>(null);

  const fetchData = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);

    // 1. workspace_members 조회
    const { data: members } = await supabase
      .from("workspace_members")
      .select("user_id, job_role, joined_at")
      .eq("workspace_id", workspaceId);

    if (!members || members.length === 0) {
      setStudents([]);
      setLoading(false);
      return;
    }

    const userIds = members.map(m => m.user_id);

    // 2. company_states 조회
    const { data: states } = await supabase
      .from("company_states")
      .select("user_id, capital, reputation, credit_score")
      .eq("workspace_id", workspaceId)
      .in("user_id", userIds);

    // 3. task_completions 개수 조회
    const { data: completions } = await supabase
      .from("task_completions")
      .select("user_id")
      .eq("workspace_id", workspaceId)
      .in("user_id", userIds);

    // 4. profiles에서 이메일 조회 (있다면)
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, display_name, email")
      .in("id", userIds);

    // 합산
    const taskCountMap: Record<string, number> = {};
    (completions ?? []).forEach(c => {
      taskCountMap[c.user_id] = (taskCountMap[c.user_id] ?? 0) + 1;
    });

    const stateMap: Record<string, { capital: number; reputation: number; credit_score: number }> = {};
    (states ?? []).forEach(s => {
      stateMap[s.user_id] = { capital: s.capital, reputation: s.reputation, credit_score: s.credit_score };
    });

    const profileMap: Record<string, { display_name?: string; email?: string }> = {};
    (profiles ?? []).forEach(p => {
      profileMap[p.id] = { display_name: p.display_name, email: p.email };
    });

    const rows: StudentRow[] = members.map(m => {
      const s = stateMap[m.user_id] ?? { capital: 0, reputation: 30, credit_score: 80 };
      const p = profileMap[m.user_id] ?? {};
      return {
        userId: m.user_id,
        email: p.email ?? p.display_name ?? m.user_id.slice(0, 8) + "...",
        jobRole: m.job_role ?? "-",
        joinedAt: m.joined_at,
        capital: s.capital,
        reputation: s.reputation,
        creditScore: s.credit_score,
        taskCount: taskCountMap[m.user_id] ?? 0,
        rank: getRank(s.reputation),
      };
    });

    setStudents(rows);
    setLastRefreshed(new Date());
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sorted = [...students].sort((a, b) => {
    if (sortBy === "reputation") return b.reputation - a.reputation;
    if (sortBy === "capital") return b.capital - a.capital;
    if (sortBy === "tasks") return b.taskCount - a.taskCount;
    return new Date(a.joinedAt).getTime() - new Date(b.joinedAt).getTime();
  });

  // 통계 요약
  const avgRep = students.length ? Math.round(students.reduce((s, r) => s + r.reputation, 0) / students.length) : 0;
  const avgCap = students.length ? Math.round(students.reduce((s, r) => s + r.capital, 0) / students.length) : 0;
  const totalTasks = students.reduce((s, r) => s + r.taskCount, 0);
  const rankDist = ["팀장", "과장", "대리", "사원", "인턴"].map(rank => ({
    rank,
    count: students.filter(s => s.rank === rank).length,
  }));

  return (
    <>
    <div className="space-y-5">
      {/* 상단 요약 카드 */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "참여 학생", value: `${students.length}명`, icon: "ri-group-line", color: "#7A9BFF" },
          { label: "평균 직급점수", value: `${avgRep}점`, icon: "ri-award-line", color: "#FF9F43" },
          { label: "평균 자본금", value: `${avgCap.toLocaleString()}만원`, icon: "ri-coin-line", color: "#00C896" },
          { label: "전체 태스크 완료", value: `${totalTasks}건`, icon: "ri-checkbox-multiple-line", color: "#E8375F" },
        ].map((card, i) => (
          <div key={i} className="bg-[#1C1F2E] border border-[#2A2D3E] rounded-xl px-4 py-3">
            <div className="flex items-center gap-2 mb-1.5">
              <i className={`${card.icon} text-sm`} style={{ color: card.color }}></i>
              <span className="text-[#5A5E72] text-[10px] uppercase tracking-widest">{card.label}</span>
            </div>
            <div className="text-[#111111] text-xl font-bold">{card.value}</div>
          </div>
        ))}
      </div>

      {/* 직급 분포 */}
      {students.length > 0 && (
        <div className="bg-[#1C1F2E] border border-[#2A2D3E] rounded-xl px-5 py-4">
          <div className="text-[#5A5E72] text-xs font-semibold uppercase tracking-widest mb-3">직급 분포</div>
          <div className="flex gap-2 flex-wrap">
            {rankDist.filter(r => r.count > 0).map(r => (
              <div key={r.rank} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${getRankColor(r.rank === "팀장" ? 85 : r.rank === "과장" ? 65 : r.rank === "대리" ? 45 : r.rank === "사원" ? 25 : 5)}`}>
                <span>{r.rank}</span>
                <span className="opacity-70">{r.count}명</span>
              </div>
            ))}
            {students.length > 0 && (
              <div className="ml-auto text-[#5A5E72] text-xs flex items-center gap-1">
                <i className="ri-time-line text-xs"></i>
                {lastRefreshed.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })} 기준
              </div>
            )}
          </div>
        </div>
      )}

      {/* 학생 목록 테이블 */}
      <div className="bg-[#1C1F2E] border border-[#2A2D3E] rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#2A2D3E] flex items-center justify-between">
          <span className="text-[#111111] text-sm font-semibold">학생별 진행 현황</span>
          <div className="flex items-center gap-2">
            <span className="text-[#5A5E72] text-xs">정렬:</span>
            {(["reputation", "capital", "tasks", "joined"] as const).map(s => (
              <button key={s} type="button" onClick={() => setSortBy(s)}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold cursor-pointer whitespace-nowrap transition-colors ${sortBy === s ? "bg-[#E8375F]/15 text-[#E8375F] border border-[#E8375F]/30" : "text-[#5A5E72] hover:text-[#888888]"}`}>
                {s === "reputation" ? "직급점수" : s === "capital" ? "자본금" : s === "tasks" ? "태스크" : "입장순"}
              </button>
            ))}
            <button type="button" onClick={fetchData}
              className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#252830] text-[#5A5E72] hover:text-white transition-colors cursor-pointer">
              <i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`}></i>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="px-5 py-12 text-center text-[#5A5E72] text-sm animate-pulse">데이터 로딩 중...</div>
        ) : sorted.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <i className="ri-group-line text-3xl text-[#2A2A2A] block mb-3"></i>
            <p className="text-[#5A5E72] text-sm">아직 입장한 학생이 없어요</p>
            <p className="text-[#3E4155] text-xs mt-1">학생들이 초대 코드로 입장하면 여기에 표시됩니다</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#252830]">
                  {["#", "학생", "직무", "직급", "직급점수", "자본금", "완료 태스크", "입장일", ""].map((h, i) => (
                    <th key={i} className="px-4 py-2.5 text-left text-[10px] text-[#3E4155] font-semibold uppercase tracking-widest">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E2130]">
                {sorted.map((s, i) => (
                  <tr
                    key={s.userId}
                    className="hover:bg-[#252830]/70 transition-colors cursor-pointer group"
                    onClick={() => setSelectedStudent(s)}
                  >
                    <td className="px-4 py-3 text-[#3E4155] text-xs">{i + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-[#252830] flex items-center justify-center text-[#5A5E72] text-xs font-bold shrink-0">
                          {s.email[0]?.toUpperCase() ?? "?"}
                        </div>
                        <span className="text-[#CCCCCC] text-xs truncate max-w-[140px] group-hover:text-white transition-colors">{s.email}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[#5A5E72] text-xs">{JOB_ROLE_LABELS[s.jobRole] ?? s.jobRole}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${getRankColor(s.reputation)}`}>
                        {s.rank}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-[#252830] rounded-full h-1.5">
                          <div className="h-1.5 rounded-full bg-[#FF9F43]/60" style={{ width: `${s.reputation}%` }}></div>
                        </div>
                        <span className="text-[#888888] text-xs tabular-nums">{s.reputation}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[#00C896] text-xs font-medium tabular-nums">{s.capital.toLocaleString()}만원</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <i className="ri-checkbox-circle-fill text-emerald-400/60 text-xs"></i>
                        <span className="text-[#888888] text-xs tabular-nums">{s.taskCount}건</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[#3E4155] text-[10px]">
                        {new Date(s.joinedAt).toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" })}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <i className="ri-arrow-right-s-line text-[#3E4155] group-hover:text-[#5A5E72] transition-colors text-sm"></i>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>

      {selectedStudent && (
        <StudentDetailModal
          student={selectedStudent}
          workspaceId={workspaceId}
          onClose={() => setSelectedStudent(null)}
        />
      )}
    </>
  );
}
